import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Heart, Star, MessageCircle, Trophy, Calendar, MapPin, Send, Instagram, Facebook, Twitter, Pencil, Share2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MatchReports({ user }) {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [showRatingModal, setShowRatingModal] = useState(null);
  const [ratings, setRatings] = useState({ writing: 0, detail: 0, entertainment: 0 });
  const [meta, setMeta] = useState({ total: 0, drafts: 0, canSeeDrafts: false });
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  // Auto-open report if reportId is in URL (from Telegram link)
  useEffect(() => {
    if (reportId && !loading) {
      fetchReportDetails(reportId);
    }
  }, [reportId, loading]);

  const fetchReports = async (keepCount) => {
    try {
      const response = await axios.get(`${API}/match-reports`,
        { params: { skip: 0, limit: Math.max(20, keepCount || 0) }, withCredentials: true });
      // The endpoint used to return a bare array; it now reports how many exist
      // in total so nothing stays hidden behind the first page.
      const data = Array.isArray(response.data)
        ? { reports: response.data, total: response.data.length }
        : response.data;
      setReports(data.reports || []);
      setMeta({ total: data.total || 0, drafts: data.drafts || 0,
                canSeeDrafts: !!data.can_see_drafts });
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to load match reports");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreReports = async () => {
    setLoadingMore(true);
    try {
      const response = await axios.get(`${API}/match-reports`,
        { params: { skip: reports.length, limit: 20 }, withCredentials: true });
      const more = response.data?.reports || [];
      setReports((prev) => [...prev, ...more]);
    } catch (error) {
      toast.error("Could not load any more reports");
    } finally {
      setLoadingMore(false);
    }
  };

  const shareOnWhatsApp = async (reportId) => {
    // Opened synchronously, inside the tap, or Safari blocks it as a popup.
    // No "noopener" here: that makes window.open return null, which left an
    // orphan tab and sent the portal's own tab off to WhatsApp.
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    try {
      const r = await axios.get(`${API}/match-reports/${reportId}/whatsapp-text`,
        { withCredentials: true });
      if (win) win.location.href = r.data.whatsapp_url;
      else window.open(r.data.whatsapp_url, "_blank", "noopener");
    } catch (e) {
      if (win) win.close();
      toast.error(e.response?.data?.detail || "Could not build the message");
    }
  };

  const fetchReportDetails = async (reportId) => {
    try {
      const response = await axios.get(`${API}/match-reports/${reportId}`, { withCredentials: true });
      setSelectedReport(response.data);
    } catch (error) {
      console.error("Error fetching report details:", error);
      toast.error("Failed to load report details");
    }
  };

  const handleLike = async (reportId) => {
    try {
      const response = await axios.post(`${API}/match-reports/${reportId}/like`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchReports(reports.length);
      if (selectedReport && selectedReport.id === reportId) {
        fetchReportDetails(reportId);
      }
    } catch (error) {
      console.error("Error liking report:", error);
      toast.error("Failed to like report");
    }
  };

  const handleRate = async () => {
    if (!showRatingModal || ratings.writing === 0 || ratings.detail === 0 || ratings.entertainment === 0) {
      toast.error("Please rate all categories (1-5 stars each)");
      return;
    }

    try {
      await axios.post(
        `${API}/match-reports/${showRatingModal}/rate`,
        ratings,
        { withCredentials: true }
      );
      toast.success("Rating submitted!");
      setShowRatingModal(null);
      setRatings({ writing: 0, detail: 0, entertainment: 0 });
      fetchReports(reports.length);
      if (selectedReport && selectedReport.id === showRatingModal) {
        fetchReportDetails(showRatingModal);
      }
    } catch (error) {
      console.error("Error rating report:", error);
      toast.error("Failed to submit rating");
    }
  };

  const handleComment = async (reportId) => {
    if (!commentText.trim()) {
      toast.error("Comment cannot be empty");
      return;
    }

    try {
      await axios.post(
        `${API}/match-reports/${reportId}/comments`,
        { content: commentText },
        { withCredentials: true }
      );
      toast.success("Comment added!");
      setCommentText("");
      fetchReportDetails(reportId);
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    }
  };

  const handleLikeComment = async (commentId, reportId) => {
    try {
      await axios.post(`${API}/match-reports/comments/${commentId}/like`, {}, { withCredentials: true });
      fetchReportDetails(reportId);
    } catch (error) {
      console.error("Error liking comment:", error);
      toast.error("Failed to like comment");
    }
  };

  const handleDeleteComment = async (commentId, reportId) => {
    if (!window.confirm("Delete this comment?")) return;

    try {
      await axios.delete(`${API}/match-reports/comments/${commentId}`, { withCredentials: true });
      toast.success("Comment deleted");
      fetchReportDetails(reportId);
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    }
  };

  const calculateAverageRatings = (ratingsObj) => {
    if (!ratingsObj || Object.keys(ratingsObj).length === 0) {
      return { writing: 0, detail: 0, entertainment: 0, count: 0 };
    }

    const values = Object.values(ratingsObj);
    const count = values.length;
    
    const sums = values.reduce(
      (acc, rating) => ({
        writing: acc.writing + rating.writing,
        detail: acc.detail + rating.detail,
        entertainment: acc.entertainment + rating.entertainment,
      }),
      { writing: 0, detail: 0, entertainment: 0 }
    );

    return {
      writing: (sums.writing / count).toFixed(1),
      detail: (sums.detail / count).toFixed(1),
      entertainment: (sums.entertainment / count).toFixed(1),
      count,
    };
  };

  const StarRating = ({ rating, onRate, label }) => {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium w-32">{label}:</span>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-6 h-6 cursor-pointer ${
                star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
              }`}
              onClick={() => onRate && onRate(star)}
            />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-500 mx-auto"></div>
          <p className="mt-4 text-slate-300 font-medium">Loading match reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate("/dashboard")}
            variant="ghost"
            className="text-yellow-500 hover:text-yellow-500"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">🏆 Match Reports</h1>
          <p className="text-slate-400">Read, rate, and discuss game reports from all teams</p>
          {meta.total > 0 && (
            <p className="text-sm text-slate-500 mt-2" data-testid="report-counts">
              Showing {reports.length} of {meta.total}
              {meta.canSeeDrafts && meta.drafts > 0 && (
                <span className="text-amber-300">
                  {" "}· {meta.drafts} written but never published
                </span>
              )}
            </p>
          )}
        </div>

        {/* Reports Feed */}
        <div className="space-y-6">
          {reports.length === 0 ? (
            <Card className="p-12 text-center">
              <Trophy className="w-16 h-16 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-400 text-lg">No match reports yet</p>
              <p className="text-slate-400 text-sm mt-2">Reports will appear here after captains submit them</p>
            </Card>
          ) : (
            reports.map((report) => {
              const avgRatings = calculateAverageRatings(report.ratings);
              const isLiked = report.likes && report.likes.includes(user.id);
              const canEdit = ["convenor", "captain", "team_captain"].includes(user.role)
                || report.author_id === user.id;

              return (
                <Card key={report.id}
                  className={`p-6 hover:shadow-lg transition-shadow ${
                    report.is_draft ? "border-amber-500/40 bg-amber-500/5" : ""}`}
                  data-testid={`report-card-${report.id}`}>
                  {report.is_draft && (
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3 pb-3 border-b border-amber-500/20">
                      <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-300"
                        data-testid={`draft-badge-${report.id}`}>
                        Draft — nobody can read this yet
                      </span>
                      {canEdit && (
                        <Button size="sm" variant="outline"
                          onClick={() => navigate(`/games?report=${report.game_id}`)}
                          data-testid={`finish-draft-${report.id}`}>
                          <Pencil className="w-3 h-3 mr-1" /> Finish and publish it
                        </Button>
                      )}
                    </div>
                  )}
                  {/* Report Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-blue-400 font-semibold">{report.team_name}</span>
                        {/* Team Social Media */}
                        {(report.team_instagram || report.team_facebook || report.team_twitter) && (
                          <>
                            <span className="text-slate-400">•</span>
                            <div className="flex items-center gap-2">
                              {report.team_instagram && (
                                <a 
                                  href={report.team_instagram.startsWith('http') ? report.team_instagram : `https://instagram.com/${report.team_instagram}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-pink-600 hover:text-pink-400"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Instagram className="w-4 h-4" />
                                </a>
                              )}
                              {report.team_facebook && (
                                <a 
                                  href={report.team_facebook.startsWith('http') ? report.team_facebook : `https://facebook.com/${report.team_facebook}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-400"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Facebook className="w-4 h-4" />
                                </a>
                              )}
                              {report.team_twitter && (
                                <a 
                                  href={report.team_twitter.startsWith('http') ? report.team_twitter : `https://twitter.com/${report.team_twitter}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sky-500 hover:text-sky-600"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Twitter className="w-4 h-4" />
                                </a>
                              )}
                            </div>
                          </>
                        )}
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-400 text-sm">
                          {new Date(report.match_date).toLocaleDateString()}
                        </span>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">{report.title}</h2>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-400">
                        <span>📝 {report.author_name}</span>
                        <span>⚽ {report.opponent}</span>
                        <span className="font-bold text-base sm:text-lg">
                          {report.score_home} - {report.score_away}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Report Content Preview */}
                  <p className="text-slate-300 mb-4 line-clamp-3">{report.content}</p>

                  {/* Ratings Display */}
                  {avgRatings.count > 0 && (
                    <div className="bg-[#0a0f1a] rounded p-3 mb-4 text-sm">
                      <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-medium text-xs sm:text-sm">Writing: {avgRatings.writing}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-medium text-xs sm:text-sm">Detail: {avgRatings.detail}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-medium text-xs sm:text-sm">Entertain: {avgRatings.entertainment}</span>
                        </div>
                        <span className="text-slate-400 text-xs">({avgRatings.count})</span>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-4 border-t">
                    {!report.is_draft && (
                      <Button
                        onClick={() => handleLike(report.id)}
                        data-testid={`like-report-${report.id}`}
                        variant="ghost"
                        size="sm"
                        className={isLiked ? "text-red-500" : "text-slate-400"}
                      >
                        <Heart className={`w-4 h-4 sm:w-5 sm:h-5 mr-1 ${isLiked ? "fill-red-500" : ""}`} />
                        {report.likes?.length || 0}
                      </Button>
                    )}

                    {!report.is_draft && (
                      <Button
                        onClick={() => setShowRatingModal(report.id)}
                        data-testid={`rate-report-${report.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-slate-400"
                      >
                        <Star className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                        Rate
                      </Button>
                    )}

                    {!report.is_draft && (
                      <Button
                        onClick={() => fetchReportDetails(report.id)}
                        data-testid={`comments-report-${report.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-slate-400"
                      >
                        <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                        <span className="hidden sm:inline">{report.comments_count || 0} Comments</span>
                        <span className="sm:hidden">{report.comments_count || 0}</span>
                      </Button>
                    )}

                    {!report.is_draft && (
                      <Button
                        onClick={() => shareOnWhatsApp(report.id)}
                        variant="ghost"
                        size="sm"
                        className="text-emerald-400"
                        data-testid={`share-report-${report.id}`}
                      >
                        <Share2 className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                        Tell the squad
                      </Button>
                    )}

                    {canEdit && !report.is_draft && (
                      <Button
                        onClick={() => navigate(`/games?report=${report.game_id}`)}
                        variant="ghost"
                        size="sm"
                        className="text-slate-400"
                        data-testid={`edit-report-${report.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                    )}

                    <Button
                      onClick={() => fetchReportDetails(report.id)}
                      size="sm"
                      className="ml-auto bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm"
                      data-testid={`read-report-${report.id}`}
                    >
                      <span className="hidden sm:inline">Read Full Report</span>
                      <span className="sm:hidden">Read</span>
                    </Button>
                  </div>
                  {report.edited_at && (
                    <p className="text-[11px] text-slate-500 mt-2"
                      data-testid={`edited-stamp-${report.id}`}>
                      Corrected {new Date(report.edited_at).toLocaleDateString()}
                      {report.edited_by_name ? ` by ${report.edited_by_name}` : ""}
                    </p>
                  )}
                </Card>
              );
            })
          )}
        </div>

        {reports.length < meta.total && (
          <div className="mt-6 text-center">
            <Button onClick={loadMoreReports} disabled={loadingMore}
              variant="outline" data-testid="load-more-reports">
              {loadingMore
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>
                : `Show more (${meta.total - reports.length} to go)`}
            </Button>
          </div>
        )}

        {/* Rating Modal */}
        {showRatingModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
            onClick={() => setShowRatingModal(null)}
          >
            <Card className="max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold mb-4">Rate This Report</h3>
              <p className="text-sm text-slate-400 mb-6">Rate each category from 1 to 5 stars</p>

              <div className="space-y-4 mb-6">
                <StarRating
                  label="Writing Quality"
                  rating={ratings.writing}
                  onRate={(star) => setRatings({ ...ratings, writing: star })}
                />
                <StarRating
                  label="Detail Level"
                  rating={ratings.detail}
                  onRate={(star) => setRatings({ ...ratings, detail: star })}
                />
                <StarRating
                  label="Entertainment"
                  rating={ratings.entertainment}
                  onRate={(star) => setRatings({ ...ratings, entertainment: star })}
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={() => setShowRatingModal(null)} variant="outline" className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleRate} className="flex-1 bg-yellow-500/100 hover:bg-yellow-400">
                  Submit Rating
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Report Detail Modal */}
        {selectedReport && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
            onClick={() => setSelectedReport(null)}
          >
            <Card className="max-w-3xl w-full my-8 p-8" onClick={(e) => e.stopPropagation()}>
              {/* Close Button */}
              <button
                onClick={() => setSelectedReport(null)}
                className="float-right text-slate-400 hover:text-slate-300 text-2xl font-bold"
              >
                ×
              </button>

              {/* Report Header */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2 text-sm text-slate-400">
                  <span className="text-blue-400 font-semibold">{selectedReport.team_name}</span>
                  <span>•</span>
                  <span>{new Date(selectedReport.match_date).toLocaleDateString()}</span>
                </div>
                <h2 className="text-3xl font-bold text-white mb-3">{selectedReport.title}</h2>
                <div className="flex items-center gap-4 text-sm text-slate-400">
                  <span>📝 {selectedReport.author_name}</span>
                  <span>⚽ vs {selectedReport.opponent}</span>
                  <span className="text-2xl font-bold text-white">
                    {selectedReport.score_home} - {selectedReport.score_away}
                  </span>
                </div>
                {selectedReport.is_draft && (
                  <div
                    className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
                    data-testid="detail-draft-banner"
                  >
                    <p className="text-sm text-amber-300">
                      Draft — only staff can see this. Members can read it, rate it and
                      comment once it is published.
                    </p>
                    <Button
                      onClick={() => navigate(`/games?report=${selectedReport.game_id}`)}
                      size="sm"
                      className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid={`detail-finish-draft-${selectedReport.id}`}
                    >
                      Finish and publish it
                    </Button>
                  </div>
                )}
              </div>

              {/* Full Content */}
              <div className="prose max-w-none mb-8">
                <p className="text-slate-300 whitespace-pre-wrap">{selectedReport.content}</p>
              </div>

              {/* Engagement Stats */}
              {!selectedReport.is_draft && (
              <div className="flex items-center gap-4 pb-6 border-b mb-6">
                <Button
                  onClick={() => handleLike(selectedReport.id)}
                  variant="ghost"
                  className={
                    selectedReport.likes?.includes(user.id) ? "text-red-500" : "text-slate-400"
                  }
                >
                  <Heart
                    className={`w-5 h-5 mr-1 ${
                      selectedReport.likes?.includes(user.id) ? "fill-red-500" : ""
                    }`}
                  />
                  {selectedReport.likes?.length || 0} Likes
                </Button>

                <Button
                  onClick={() => setShowRatingModal(selectedReport.id)}
                  variant="ghost"
                  className="text-slate-400"
                >
                  <Star className="w-5 h-5 mr-1" />
                  Rate This Report
                </Button>
              </div>
              )}

              {/* Comments Section */}
              {!selectedReport.is_draft && (
              <div>
                <h3 className="text-xl font-bold mb-4">
                  💬 Comments ({selectedReport.comments?.length || 0})
                </h3>

                {/* Add Comment */}
                <div className="mb-6">
                  <Textarea
                    placeholder="Add your comment... (use @username to mention someone)"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    className="mb-2"
                    rows={3}
                  />
                  <Button
                    onClick={() => handleComment(selectedReport.id)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Post Comment
                  </Button>
                </div>

                {/* Comments List */}
                <div className="space-y-4">
                  {selectedReport.comments?.map((comment) => (
                    <div key={comment.id} className="bg-[#0a0f1a] rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="font-semibold text-white">{comment.user_name}</span>
                          <span className="text-slate-400 text-sm ml-2">
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        {comment.user_id === user.id && (
                          <Button
                            onClick={() => handleDeleteComment(comment.id, selectedReport.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-400"
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                      <p className="text-slate-300 mb-2">{comment.content}</p>
                      <Button
                        onClick={() => handleLikeComment(comment.id, selectedReport.id)}
                        variant="ghost"
                        size="sm"
                        className={
                          comment.likes?.includes(user.id) ? "text-red-500" : "text-slate-400"
                        }
                      >
                        <Heart
                          className={`w-4 h-4 mr-1 ${
                            comment.likes?.includes(user.id) ? "fill-red-500" : ""
                          }`}
                        />
                        {comment.likes?.length || 0}
                      </Button>
                    </div>
                  ))}

                  {(!selectedReport.comments || selectedReport.comments.length === 0) && (
                    <p className="text-center text-slate-400 py-8">
                      No comments yet. Be the first to comment!
                    </p>
                  )}
                </div>
              </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
